import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const MessagesTab = ({
  contacts,
  deleteContact,
  getContactStatusColor,
  messageReplyText,
  replyToMessage,
  replyingToMessage,
  setMessageReplyText,
  setReplyingToMessage,
  updateContactStatus,
}) => (
          <TabsContent value="messages">
            <div className="space-y-6">
              {/* Messages Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Total Messages</CardTitle>
                    <Mail className="h-5 w-5 text-blue-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{contacts.length}</div>
                  </CardContent>
                </Card>

                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">New Messages</CardTitle>
                    <Mail className="h-5 w-5 text-red-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-500">
                      {contacts.filter(c => c.status === 'new').length}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Replied</CardTitle>
                    <Reply className="h-5 w-5 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-500">
                      {contacts.filter(c => c.status === 'replied').length}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Messages List */}
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#FFC107]" />
                    Contact Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {contacts.length === 0 ? (
                    <div className="text-center py-12">
                      <Mail className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No messages yet</p>
                      <p className="text-zinc-600 text-sm">Customer messages will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className={`bg-[#0B0B0C] rounded-lg p-4 border ${
                            contact.status === 'new' ? 'border-blue-500/50' : 'border-[#27272A]'
                          }`}
                          data-testid={`contact-${contact.id}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-white">{contact.name}</h4>
                                <Badge className={getContactStatusColor(contact.status)}>
                                  {contact.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-zinc-500">{contact.email}</p>
                              {contact.phone && (
                                <p className="text-sm text-zinc-500">{contact.phone}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={contact.status}
                                onValueChange={(value) => updateContactStatus(contact.id, value)}
                              >
                                <SelectTrigger className="w-28 bg-[#1C1C22] border-zinc-700 h-8" data-testid={`contact-status-${contact.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141417] border-[#27272A]">
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="read">Read</SelectItem>
                                  <SelectItem value="replied">Replied</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                                onClick={() => deleteContact(contact.id)}
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {contact.subject && (
                            <p className="text-[#FFC107] font-medium text-sm mb-2">
                              Subject: {contact.subject}
                            </p>
                          )}
                          <p className="text-zinc-300 text-sm bg-[#141417] p-3 rounded-lg">
                            {contact.message}
                          </p>
                          
                          {/* Admin Reply Section */}
                          {contact.admin_reply ? (
                            <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Reply className="h-4 w-4 text-green-500" />
                                <span className="text-xs font-semibold text-green-500">Your Reply</span>
                              </div>
                              <p className="text-zinc-300 text-sm">{contact.admin_reply}</p>
                              {contact.replied_at && (
                                <p className="text-xs text-zinc-600 mt-2">
                                  Replied on {new Date(contact.replied_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          ) : replyingToMessage === contact.id ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={messageReplyText}
                                onChange={(e) => setMessageReplyText(e.target.value)}
                                placeholder="Write your reply... (will be emailed to customer)"
                                className="bg-[#141417] border-zinc-700 min-h-[80px]"
                                data-testid={`message-reply-input-${contact.id}`}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => replyToMessage(contact.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`send-message-reply-${contact.id}`}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Send Reply
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setReplyingToMessage(null); setMessageReplyText(''); }}
                                  className="border-zinc-700"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setReplyingToMessage(contact.id)}
                              className="mt-3 border-zinc-700 hover:bg-[#25252E]"
                              data-testid={`reply-message-btn-${contact.id}`}
                            >
                              <Reply className="h-4 w-4 mr-1" />
                              Reply to Customer
                            </Button>
                          )}
                          
                          <p className="text-xs text-zinc-600 mt-3">
                            {new Date(contact.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default MessagesTab;
